import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./JourneyPage.module.css";
import QuizInJourney from "../journey/QuizInJourney";
import HalalvsharamInJourney from "../journey/HalalvsharamInJourney";
import FillInTheBlanksInJourney from "../journey/FillInTheBlanksInJourney";
import FlashcardsInJourney from "../journey/FlashcardsInJourney";
import FigureMatchInJourney from "../journey/FigureMatchInJourney";

// Journey Completion Screen Component - UNCHANGED
const JourneyCompletionScreen = ({ score, totalActivities, onFinish }) => {
    useEffect(() => {
        const timer = setTimeout(onFinish, 20000);
        return () => clearTimeout(timer);
    }, [onFinish]);

    return (
        <div className={styles.completionScreen}>
            <div className={styles.completionBox}>
                <div className={styles.completionAnimation}>
                    <span role="img" aria-label="Completion Icon" className={styles.starAnimation}>✨</span>
                    <span role="img" aria-label="Completion Icon" className={styles.starAnimation}>🕌</span>
                    <span role="img" aria-label="Completion Icon" className={styles.starAnimation}>✨</span>
                </div>

                <h2 className={styles.completionTitle}>Alhamdulillah! Journey Completed!</h2>
                <p className={styles.completionSubtitle}>You completed {totalActivities} activities today.</p>

                <div className={styles.summaryBox}>
                    <h3 className={styles.summaryTitle}>The Barakah of Daily Knowledge 🤲</h3>
                    <p>
                        Masha'Allah! By committing to these daily lessons, you are consistently nurturing your relationship with your Deen.
                        **Small, consistent acts are beloved by Allah.** Keep returning to DeenRoute to solidify your knowledge and earn continuous rewards.
                    </p>
                </div>

                <p className={styles.redirectMessage}>Redirecting you to the home screen shortly...</p>

                <button onClick={onFinish} className={styles.finishButton}>
                    Return Home Now
                </button>
            </div>
        </div>
    );
};

// Islamic Journey Route Component - SIMPLIFIED (Mini-map logic removed)
const IslamicJourneyRoute = ({
                                 lesson,
                                 currentIndex,
                                 onNodeClick,
                                 className,
                                 showLoadingRing,
                                 celebratingNodeIndex = null // KEPT: For success animation
                             }) => {
    const getPathPoints = (nodeCount) => {
        const points = [];
        const svgWidth = 300;
        const svgHeight = Math.max(400, nodeCount * 120);
        const centerX = svgWidth / 2;

        for (let i = 0; i < nodeCount; i++) {
            const progress = i / (nodeCount - 1 || 1);
            const y = 80 + progress * (svgHeight - 160);
            let x;

            if (i % 4 === 0) x = centerX;
            else if (i % 4 === 1) x = centerX + 60;
            else if (i % 4 === 2) x = centerX;
            else x = centerX - 60;

            points.push({ x, y });
        }
        return points;
    };

    const createCurvedPath = (points) => {
        if (points.length < 2) return "";
        let path = `M ${points[0].x} ${points[0].y}`;

        for (let i = 1; i < points.length; i++) {
            const current = points[i];
            const prev = points[i - 1];
            const midX = (prev.x + current.x) / 2;
            const midY = (prev.y + current.y) / 2;
            path += ` Q ${midX + (current.x > prev.x ? 15 : -15)} ${midY} ${current.x} ${current.y}`;
        }

        return path;
    };

    const points = getPathPoints(lesson.length);
    const pathString = createCurvedPath(points);
    const svgHeight = Math.max(400, lesson.length * 120);

    // Celebration logic KEPT
    const getIslamicSymbol = (activityType, isCompleted, isCurrent, isCelebrating) => {
        const symbols = {
            quiz: "☪️",
            halalvsharam: "⭐",
            fillintheblanks: "📿",
            flashcards: "⚡️",
            figurematch: "🕌",
        };

        // Show checkmark during celebration
        if (isCelebrating) return "✅";
        if (isCompleted) return "✨";
        if (isCurrent) return "🌟";
        return symbols[activityType] || "☪️";
    };

    return (
        <div className={`${styles.journeyRouteContainer} ${className || ""}`}>
            {/* Header always visible */}
            <div className={styles.journeyHeader}>
                <h2 className={styles.journeyTitle}>رحلة العلم - Journey of Knowledge</h2>
                <div className={styles.progressIndicator}>
                    {currentIndex} of {lesson.length} completed
                </div>
            </div>

            <div className={styles.routeSvgContainer}>
                <svg
                    viewBox={`0 0 300 ${svgHeight}`}
                    className={styles.journeySvg}
                    preserveAspectRatio="xMidYMin meet"
                >
                    <defs>
                        <pattern id="islamicPattern" width="40" height="40" patternUnits="userSpaceOnUse">
                            <circle cx="20" cy="20" r="1.5" fill="rgba(255,215,0,0.15)" />
                        </pattern>
                        <filter id="currentGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    <rect width="100%" height="100%" fill="url(#islamicPattern)" />

                    {pathString && (
                        <>
                            {/* Completed path segments - solid green */}
                            <path
                                d={pathString}
                                fill="none"
                                stroke="#50C878"
                                strokeWidth="4"
                                strokeDasharray={currentIndex > 0 ? `${currentIndex * 200} 10000` : "0,1000"}
                                strokeLinecap="round"
                                style={{
                                    transition: "stroke-dasharray 0.5s ease",
                                    transitionDelay: celebratingNodeIndex !== null ? "0.3s" : "0s"
                                }}
                            />
                            {/* Incomplete path - gray dashed */}
                            <path
                                d={pathString}
                                fill="none"
                                stroke="rgba(160,160,160,0.3)"
                                strokeWidth="2"
                                strokeDasharray="4,8"
                                strokeLinecap="round"
                            />
                        </>
                    )}

                    {points.map((pt, index) => {
                        const isCompleted = index < currentIndex;
                        const isCurrent = index === currentIndex;
                        const isCelebrating = index === celebratingNodeIndex; // Check if this node is celebrating

                        return (
                            <g key={index} className={styles.journeyNodeGroup}>
                                <circle className="journeyNodeShadow" cx={pt.x + 2} cy={pt.y + 2} r="24"
                                        fill="rgba(0,0,0,0.4)"/>

                                <text
                                    x={pt.x}
                                    y={pt.y + 6}
                                    textAnchor="middle"
                                    fontSize="18"
                                    fill="white"
                                    className={styles.nodeSymbol}
                                >
                                    {getIslamicSymbol(lesson[index]?.type, isCompleted, isCurrent, isCelebrating)}
                                </text>

                                {/* Labels always visible */}
                                <>
                                    <text
                                        x={pt.x + 35}
                                        y={pt.y - 10}
                                        fontSize="14"
                                        fill={isCurrent ? "#FFD700" : isCompleted ? "#50C878" : "#9CA3AF"}
                                        fontWeight="bold"
                                    >
                                        {index + 1}
                                    </text>
                                    <text
                                        x={pt.x + 40}
                                        y={pt.y + 8}
                                        fontSize="11"
                                        fill={isCurrent ? "#FFD700" : isCompleted ? "#50C878" : "#9CA3AF"}
                                    >
                                        {lesson[index]?.type || "activity"}
                                    </text>
                                </>

                                <circle
                                    cx={pt.x}
                                    cy={pt.y}
                                    r="22"
                                    fill={isCompleted ? "#50C878" : isCurrent ? "#FFD700" : "#6B7280"}
                                    stroke={isCompleted ? "#2E7D32" : isCurrent ? "#B8860B" : "#4B5563"}
                                    strokeWidth="3"
                                    filter={isCurrent ? "url(#currentGlow)" : "none"}
                                    className={`${styles.journeyNode} ${
                                        isCelebrating ? styles.celebrating : // Celebration class
                                            isCompleted ? styles.completed :
                                                isCurrent && showLoadingRing ? styles.loading :
                                                    isCurrent ? styles.current :
                                                        styles.locked
                                    }`}
                                    onClick={() => isCurrent && onNodeClick(index)} // Clicks always enabled for current node
                                    style={{
                                        cursor: isCurrent ? "pointer" : "default",
                                        transition: "all 0.3s ease",
                                    }}
                                />

                                {/* Click target always present */}
                                <circle
                                    cx={pt.x}
                                    cy={pt.y}
                                    r="28"
                                    fill="transparent"
                                    onClick={() => isCurrent && onNodeClick(index)}
                                    style={{ cursor: isCurrent ? "pointer" : "default" }}
                                />

                                {isCurrent && (
                                    <circle
                                        cx={pt.x}
                                        cy={pt.y}
                                        r="22"
                                        fill="none"
                                        stroke="#FFD700"
                                        strokeWidth="2"
                                        opacity="0.6"
                                        className={styles.pulseRing}
                                    />
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

// Activities Pool - UNCHANGED
const activitiesPool = {
    quiz: (config, onComplete, isLast) => (
        <QuizInJourney
            key="quiz"
            questionsCount={config.questions}
            onComplete={onComplete}
            isLast={isLast}
        />
    ),
    halalvsharam: (config, onComplete, isLast) => (
        <HalalvsharamInJourney
            key="halalvsharam"
            questionsCount={config.questions}
            onComplete={onComplete}
            isLast={isLast}
        />
    ),
    fillintheblanks: (config, onComplete, isLast) => (
        <FillInTheBlanksInJourney
            key="fillintheblanks"
            questionsCount={config.questions}
            onComplete={onComplete}
            isLast={isLast}
        />
    ),
    flashcards: (config, onComplete, isLast) => (
        <FlashcardsInJourney
            key="flashcards"
            config={config}
            onComplete={onComplete}
            isLast={isLast}
        />
    ),
    figurematch: (config, onComplete, isLast) => (
        <FigureMatchInJourney
            key="figurematch"
            questionsCount={config.pairs}
            onComplete={onComplete}
            isLast={isLast}
        />
    ),
};

export default function JourneyPage() {
    // EXISTING STATE
    const [timeChoice, setTimeChoice] = useState(null);
    const [lesson, setLesson] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [showFinalSequence, setShowFinalSequence] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [journeyScore, setJourneyScore] = useState(0);
    const [showLoadingRing, setShowLoadingRing] = useState(false);

    // SIMPLIFIED STATE: Removed miniMapVisible. Retained transitionState and celebratingNodeIndex.
    const [transitionState, setTransitionState] = useState('map-active');
    // States: 'map-active' | 'map-exiting' | 'activity-active' | 'activity-exiting' | 'celebrating'

    const [celebratingNodeIndex, setCelebratingNodeIndex] = useState(null);

    const navigate = useNavigate();

    const handleRedirect = useCallback(() => {
        navigate("/home");
    }, [navigate]);

    // EXISTING EFFECT - Unchanged
    useEffect(() => {
        if (showActivity || showFinalSequence) {
            document.body.style.paddingTop = "0";
            // Keep global document overflow enabled so activity content can scroll on small screens.
            // Previously we set `overflow = 'hidden'` here which prevented vertical scrolling of activity content.
            document.body.style.overflow = "auto";
            document.documentElement.style.overflow = "auto";
        } else {
            document.body.style.paddingTop = "65px";
            document.body.style.overflow = "auto";
            document.documentElement.style.overflow = "auto";
        }
        return () => {
            document.body.style.paddingTop = "65px";
            document.body.style.overflow = "auto";
            document.documentElement.style.overflow = "auto";
        };
    }, [showActivity, showFinalSequence]);

    // EXISTING EFFECT - Simplified reset
    useEffect(() => {
        if (timeChoice) {
            setLesson(composeLesson(timeChoice));
            setCurrentIndex(0);
            setCompleted(false);
            setShowActivity(false);
            setShowFinalSequence(false);
            setTransitionState('map-active'); // Reset transition state
            setCelebratingNodeIndex(null); // Reset celebration
        }
    }, [timeChoice]);

    // REFACTORED: Node click without mini-map logic
    const handleNodeClick = () => {
        // Show loading ring for immediate feedback
        setShowLoadingRing(true);

        // TRANSITION SEQUENCE:
        // 1. Start map exit animation (blur + fade + scale)
        setTransitionState('map-exiting');

        // 2. After 300ms, hide map and show activity
        setTimeout(() => {
            setTransitionState('activity-active');
            setShowActivity(true);
            setShowLoadingRing(false);
        }, 300);
    };

    // REFACTORED: Activity completion with celebration sequence (logic unchanged)
    const handleCompleteActivity = useCallback(() => {
        // TRANSITION SEQUENCE:
        // 1. Start activity exit animation (slide up)
        setTransitionState('activity-exiting');

        // 2. After 300ms, hide activity and move to celebration state
        setTimeout(() => {
            setShowActivity(false);
            setTransitionState('celebrating');
            setCelebratingNodeIndex(currentIndex); // Start celebration on completed node
        }, 100);

        // 3. After 600ms total, update score and move to next activity
        setTimeout(() => {
            setJourneyScore(prev => prev + 1);

            if (currentIndex < lesson.length - 1) {
                setCurrentIndex((prev) => prev + 1);
            } else {
                setCompleted(true);
            }
        }, 600);

        // 4. After 1400ms total, end celebration and return to active map state
        setTimeout(() => {
            setCelebratingNodeIndex(null);
            setTransitionState('map-active');
        }, 1400);
    }, [currentIndex, lesson.length]);

    // EXISTING EFFECT - Unchanged
    useEffect(() => {
        if (completed) {
            setShowFinalSequence(true);
        }
    }, [completed]);

    // REFACTORED: Determine CSS classes based on transition state (mini-map class removed)
    const getJourneyMapClasses = () => {
        let classes = styles.journeyRouteContainer;

        if (transitionState === 'map-exiting' || transitionState === 'activity-active' || transitionState === 'activity-exiting') {
            // Hide the map completely when activity is running or during transition to/from it
            classes += ` ${styles.mapHidden}`;
        } else if (transitionState === 'celebrating') {
            classes += ` ${styles.mapEntering}`;
        }

        return classes;
    };

    const getActivityClasses = () => {
        let classes = styles.activityWrapper;

        // Apply animation class based on when component mounts/unmounts
        if (showActivity) {
            classes += ` ${styles.activitySlideEnter}`;
        }

        return classes;
    };

    return (
        <div className={styles.journeyBackground}>
            {/* Time Selector - UNCHANGED */}
            {!timeChoice && (
                <div className={styles.selector}>
                    <h1>🕌 Welcome to the DeenRoute Journey</h1>
                    <p>How many minutes would you like to spend seeking knowledge today?</p>
                    <div className={styles.buttonRow}>
                        <button onClick={() => setTimeChoice("3")} className={styles.timeButton}>3 mins</button>
                        <button onClick={() => setTimeChoice("5")} className={styles.timeButton}>5 mins</button>
                        <button onClick={() => setTimeChoice("10")} className={styles.timeButton}>10 mins</button>
                    </div>
                </div>
            )}

            {/* REFACTORED: Journey Map - Renders when a lesson is active and not completed */}
            {timeChoice && lesson.length > 0 && !completed && !showFinalSequence && (
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // Map should be hidden entirely when activity is showing
                    opacity: showActivity ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                    pointerEvents: showActivity ? 'none' : 'auto',
                }}>
                    <IslamicJourneyRoute
                        lesson={lesson}
                        currentIndex={currentIndex}
                        onNodeClick={handleNodeClick}
                        showLoadingRing={showLoadingRing}
                        className={getJourneyMapClasses()}
                        celebratingNodeIndex={celebratingNodeIndex}
                    />
                </div>
            )}

            {/* REFACTORED: Activity - Renders when showActivity is true */}
            {timeChoice && showActivity && !completed && lesson.length > 0 && (
                <div className={getActivityClasses()}>
                    {activitiesPool[lesson[currentIndex].type](
                        lesson[currentIndex].config,
                        handleCompleteActivity,
                        currentIndex === lesson.length - 1
                    )}
                </div>
            )}

            {/* Final Completion Sequence - UNCHANGED */}
            {showFinalSequence && (
                <JourneyCompletionScreen
                    score={journeyScore}
                    totalActivities={lesson.length}
                    onFinish={handleRedirect}
                />
            )}

            {/* CSS for completion screen - UNCHANGED */}
            <style>{`
  .completionScreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(8px);
    z-index: 10000;
  }

  .completionBox {
    background: rgba(255, 255, 255, 0.95);
    color: #1a1a1a;
    padding: 40px;
    border-radius: 20px;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
    max-width: 500px;
    width: 90%;
    border: 3px solid #50C878;
  }

  .completionTitle {
    font-size: 2.5rem;
    color: #1a5f3d;
    margin-bottom: 10px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(80, 200, 120, 0.3);
  }

  .completionSubtitle {
    font-size: 1.2rem;
    color: #2d2d2d;
    margin-bottom: 20px;
    font-weight: 500;
  }

  .starAnimation {
    font-size: 3rem;
    display: inline-block;
    margin: 0 15px;
    animation: spin 3s linear infinite;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  .summaryBox {
    background: rgba(80, 200, 120, 0.1);
    padding: 20px;
    border-radius: 10px;
    margin: 20px 0;
    text-align: left;
    border-left: 5px solid #50C878;
  }

  .summaryBox p {
    color: #2d2d2d;
    line-height: 1.6;
  }

  .summaryBox strong {
    color: #1a5f3d;
    font-weight: 600;
  }

  .summaryTitle {
    color: #1a5f3d;
    font-size: 1.1rem;
    margin-bottom: 10px;
    font-weight: bold;
  }

  .redirectMessage {
    color: #1a5f3d;
    margin-top: 20px;
    font-weight: 500;
  }

  .finishButton {
    background: linear-gradient(135deg, #50C878, #2E7D32);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: bold;
    transition: all 0.3s;
    margin-top: 15px;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    box-shadow: 0 4px 15px rgba(80, 200, 120, 0.4);
  }

  .finishButton:hover {
    background: linear-gradient(135deg, #2E7D32, #1B5E20);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(80, 200, 120, 0.5);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`}</style>
        </div>
    );
}

// Compose lessons - UNCHANGED
function composeLesson(timeChoice) {
    const CORE_ACTIVITIES = ["quiz", "halalvsharam", "fillintheblanks", "figurematch"];
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

    const countMap = { "3": 2, "5": 3, "10": 4 };
    const qMap = { "3": [2, 2], "5": [3, 3, 3], "10": [5, 4, 3, 2] };
    const figureMatchPairs = { "3": 2, "5": 2, "10": 3 };
    const flashcardSurahs = [1, 108, 110, 112, 114];

    const count = countMap[timeChoice] || 0;
    let activityTypes = [];
    let shuffledCore = shuffle(CORE_ACTIVITIES);
    activityTypes = shuffledCore.slice(0, Math.min(count, CORE_ACTIVITIES.length));

    if (timeChoice === "5" || timeChoice === "10") {
        if (count > 0) {
            const flashcardSlotIndex = Math.floor(Math.random() * activityTypes.length);
            activityTypes[flashcardSlotIndex] = "flashcards";
        }
    }

    while (activityTypes.length < count) {
        activityTypes.push(shuffledCore[activityTypes.length % CORE_ACTIVITIES.length]);
    }

    return activityTypes.slice(0, count).map((type, i) => {
        if (type === "flashcards") {
            const randomSurah = flashcardSurahs[Math.floor(Math.random() * flashcardSurahs.length)];
            return { type: "flashcards", config: { surah: randomSurah } };
        }

        if (type === "figurematch") {
            return {
                type: "figurematch",
                config: { pairs: figureMatchPairs[timeChoice] }
            };
        }

        return { type, config: { questions: qMap[timeChoice][i] || 3 } };
    });
}